import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { pinElementToBottom } from '@renderer/utils/pin-to-bottom'
import { AUTO_SCROLL_ENGAGE_DISTANCE_PX, resolveAutoFollow } from './auto-follow'
import { clearReadingAnchor, holdReadingAnchor, noteReaderScroll, recordReadingAnchor } from './reading-anchor'

interface UseBodyScrollOptions {
  threshold?: number
  initialAutoScroll?: boolean
  /**
   * Re-pin to the bottom whenever the content grows, for a body whose bottom
   * edge is the live edge of a stream. Read fresh on every resize rather than
   * frozen at mount, so a meeting ending hands the position back to the reader.
   *
   * Off by default. `autoScrollEnabledRef` alone can't gate this: it only means
   * the viewport is within `threshold` of the bottom, which is true of anyone
   * reading the end of a finished meeting, and every summary or section that
   * grows under them would yank them down.
   *
   * While on and the reader has scrolled away from the live edge, their place
   * is held against the row at the top of the viewport instead: the scroller
   * runs with browser scroll anchoring off, so nothing else stops a line
   * regrouping, a speaker relabel or the captured actions growing above them
   * from moving what they're reading.
   */
  followGrowth?: boolean
}

interface UseBodyScrollReturn {
  showScrollButton: boolean
  scrollToBottom: () => void
  /**
   * Snap to the bottom and hold it there while the view settles. Returns whether
   * a pin was actually armed — false while the scroller doesn't exist yet, which
   * a caller latching "the initial pin is done" has to distinguish from success.
   */
  pinToBottom: () => boolean
  onContentChange: () => void
  /** Hands the position to the reader, as a wheel-up does, before a jump to somewhere other than the live edge. */
  stopFollowing: () => void
  scrollRef: (node: HTMLDivElement | undefined) => void
}

/**
 * How long a pin holds the bottom after an open, regardless of the height going
 * quiet. Every `pinToBottom` caller is an open or a mode switch, and landing at
 * the bottom is the *destination* there, not a catch-up worth watching.
 *
 * Long enough to cover a backlog arriving in chunks and a virtualizer swapping
 * estimated row heights for measured ones.
 */
const OPEN_SETTLE_MS = 800

export function useBodyScroll(options: UseBodyScrollOptions = {}): UseBodyScrollReturn {
  const { threshold = 150, initialAutoScroll = true, followGrowth = false } = options

  const autoScrollEnabledRef = useRef(initialAutoScroll)
  const followGrowthRef = useRef(followGrowth)
  followGrowthRef.current = followGrowth
  const isProgrammaticScrollRef = useRef(false)
  const intentionalAutoScrollRef = useRef(false)
  const scrollEndCleanupRef = useRef<(() => void) | null>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)

  // Store the scroller via a callback ref so we react to availability
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)

  // Callback ref for FadingScrollable (receives HTMLDivElement | undefined from useImperativeHandle)
  const scrollRef = useCallback((node: HTMLDivElement | undefined) => {
    setScrollElement(node ?? null)
  }, [])

  const resetProgrammaticScrollOnEnd = useCallback(() => {
    // Clean up any previous listener
    scrollEndCleanupRef.current?.()

    if (!scrollElement) {
      isProgrammaticScrollRef.current = false
      return
    }

    const fallbackTimeout = setTimeout(() => {
      isProgrammaticScrollRef.current = false
      if (intentionalAutoScrollRef.current) {
        intentionalAutoScrollRef.current = false
      } else {
        updateButtonsRef.current()
      }
      scrollEndCleanupRef.current = null
    }, 2000)

    const onScrollEnd = () => {
      clearTimeout(fallbackTimeout)
      isProgrammaticScrollRef.current = false
      if (intentionalAutoScrollRef.current) {
        intentionalAutoScrollRef.current = false
      } else {
        updateButtonsRef.current()
      }
      scrollEndCleanupRef.current = null
    }

    scrollElement.addEventListener('scrollend', onScrollEnd, { once: true })

    scrollEndCleanupRef.current = () => {
      clearTimeout(fallbackTimeout)
      scrollElement.removeEventListener('scrollend', onScrollEnd)
      scrollEndCleanupRef.current = null
    }
  }, [scrollElement])

  const getDistanceFromBottom = useCallback(() => {
    if (!scrollElement) return 0
    const { scrollTop, scrollHeight, clientHeight } = scrollElement
    return scrollHeight - scrollTop - clientHeight
  }, [scrollElement])

  const pinArmedRef = useRef(false)
  const releasePinRef = useRef<(() => void) | null>(null)

  // Deliberately does not set isProgrammaticScrollRef: a pin lands at distance 0,
  // so updateButtons already agrees with what we set here.
  const syncPinnedButtons = useCallback(() => {
    autoScrollEnabledRef.current = true
    setShowScrollButton(false)
  }, [])

  // Recorded when follow disengages and on every scroll of the reader's own, and
  // dropped while following: a place recorded before the content last moved
  // would undo that move on release.
  const recordReadingPosition = useCallback(() => {
    if (scrollElement) recordReadingAnchor(scrollElement)
  }, [scrollElement])

  const holdReadingPosition = useCallback(() => {
    if (scrollElement) holdReadingAnchor(scrollElement)
  }, [scrollElement])

  const pinNow = useCallback(() => {
    const el = scrollElement
    if (!el) return
    clearReadingAnchor(el)
    el.scrollTo({ top: el.scrollHeight, behavior: 'instant' })
    syncPinnedButtons()
  }, [scrollElement, syncPinnedButtons])

  const disarmPin = useCallback(() => {
    releasePinRef.current?.()
  }, [])

  // After disarming, whose release re-reads the distance and would re-engage follow at the bottom
  const stopFollowing = useCallback(() => {
    disarmPin()
    autoScrollEnabledRef.current = false
    recordReadingPosition()
  }, [disarmPin, recordReadingPosition])

  /**
   * Snap to the bottom and hold it there while the view settles — the sidepanel
   * unmounts the whole tree on close, so a reopened View remounts against content
   * that hasn't arrived yet. The armed window re-pins on growth every frame, so
   * late content lands *already* at the bottom.
   */
  const pinToBottom = useCallback(() => {
    const el = scrollElement
    // The scroller arrives via a callback ref, so it is still null through the
    // whole mount commit — every caller's first attempt lands here.
    if (!el) return false
    releasePinRef.current?.()
    pinArmedRef.current = true
    releasePinRef.current = pinElementToBottom(el, {
      minMs: OPEN_SETTLE_MS,
      // The user taking over always wins (wheel-up flips autoScrollEnabledRef).
      shouldContinue: () => autoScrollEnabledRef.current,
      onPin: syncPinnedButtons,
      onRelease: () => {
        pinArmedRef.current = false
        releasePinRef.current = null
        // Scroll events were ignored for the whole window, so reconcile once
        // against where the pin actually left us.
        updateButtonsRef.current()
      },
    })
    return true
  }, [scrollElement, syncPinnedButtons])

  const scrollToBottom = useCallback(() => {
    if (!scrollElement) return
    disarmPin()
    isProgrammaticScrollRef.current = true
    scrollElement.scrollTo({ top: scrollElement.scrollHeight, behavior: 'smooth' })
    autoScrollEnabledRef.current = true
    intentionalAutoScrollRef.current = true
    setShowScrollButton(false)
    resetProgrammaticScrollOnEnd()
  }, [scrollElement, disarmPin, resetProgrammaticScrollOnEnd])

  // Instant, never smooth. Callers fire this from the layout pass that inserted
  // the content, so the new lines paint already at the bottom. A smooth scroll
  // paints the growth first and animates after, which on a live transcript
  // reads as a down-jump every turn, and each partial tick restarts the
  // animation before it lands. A reader who scrolled away is held in place in
  // that same pass instead.
  const onContentChange = useCallback(() => {
    if (!scrollElement) return
    if (autoScrollEnabledRef.current) pinNow()
    else if (followGrowthRef.current) holdReadingPosition()
  }, [scrollElement, pinNow, holdReadingPosition])

  const updateButtons = useCallback(() => {
    if (!scrollElement) return
    const distance = getDistanceFromBottom()

    setShowScrollButton(distance > threshold)

    const follow = resolveAutoFollow(distance, autoScrollEnabledRef.current, {
      engage: AUTO_SCROLL_ENGAGE_DISTANCE_PX,
      disengage: threshold,
    })
    if (autoScrollEnabledRef.current && !follow) recordReadingPosition()
    autoScrollEnabledRef.current = follow
  }, [scrollElement, getDistanceFromBottom, threshold, recordReadingPosition])

  // Store updateButtons in a ref so the ResizeObserver always calls the latest version
  const updateButtonsRef = useRef(updateButtons)
  updateButtonsRef.current = updateButtons

  // Initial pin — runs once the scroller exists. A layout effect so it lands before
  // the first paint rather than after it, and pinToBottom (not a bare jump) so the
  // content that arrives over the following frames is placed at the bottom too.
  const hasInitialPinRef = useRef(false)
  useLayoutEffect(() => {
    if (!initialAutoScroll || hasInitialPinRef.current || !scrollElement) return
    hasInitialPinRef.current = true
    pinToBottom()
  }, [scrollElement, initialAutoScroll, pinToBottom])

  // ResizeObserver — runs when scrollElement becomes available
  useEffect(() => {
    if (!scrollElement) return

    const observer = new ResizeObserver(() => {
      // A pin owns the position for as long as it is armed. Content growing
      // under it is the case it exists for — the correction lands on the next
      // frame — but in the gap between the two this reads as a big distance from
      // the bottom, and `updateButtons` would take that for the user having
      // scrolled away: auto-follow off, jump-to-latest up, and `shouldContinue`
      // then releases the very window that was about to fix it. That is what
      // left a reopened transcript sitting just short of the last turn
      // (HIG-5978).
      if (isProgrammaticScrollRef.current || pinArmedRef.current) return
      // ResizeObserver runs after layout and before paint, so re-pinning here
      // means a frame where the content grew never paints scrolled short. The
      // case that needs it is the virtualizer swapping a row's estimated height
      // for its measured one a beat after onContentChange pinned. Nothing else
      // re-pins until the next transcript tick. The same pre-paint timing holds
      // a reader in place against growth no transcript tick announces.
      if (followGrowthRef.current) {
        if (autoScrollEnabledRef.current) {
          pinNow()
          return
        }
        holdReadingPosition()
      }
      updateButtonsRef.current()
    })

    if (scrollElement.firstElementChild) {
      observer.observe(scrollElement.firstElementChild)
    }
    observer.observe(scrollElement)

    return () => observer.disconnect()
  }, [scrollElement, pinNow, holdReadingPosition])

  // Scroll and wheel events
  useEffect(() => {
    if (!scrollElement) return

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        if (autoScrollEnabledRef.current) recordReadingPosition()
        autoScrollEnabledRef.current = false
        const distance = getDistanceFromBottom()
        if (distance > threshold) {
          setShowScrollButton(true)
        }
      } else if (e.deltaY > 0) {
        const distance = getDistanceFromBottom()
        if (distance <= AUTO_SCROLL_ENGAGE_DISTANCE_PX) {
          autoScrollEnabledRef.current = true
          setShowScrollButton(false)
        }
      }
    }

    const handleScroll = () => {
      // The reader moving their own place, as opposed to a hold's correction
      if (followGrowthRef.current && !autoScrollEnabledRef.current) noteReaderScroll(scrollElement)
      if (isProgrammaticScrollRef.current) return
      // `updateButtons` can't run while a pin is armed, and this is the path that
      // kept a reopened transcript short of its last turn: the pin's own
      // `scrollTo` fires this, and the scroll event is dispatched a frame later —
      // after the virtualizer has swapped estimated row heights for measured
      // ones. Rows here are deliberate underestimates, so a wrapped three-line
      // turn grows the document by far more than `threshold` in that gap, and
      // reading the distance there takes the pin's own landing for the user
      // having scrolled away: auto-follow off, jump-to-latest up, and
      // `shouldContinue` tears down the window whose next frame would have
      // re-pinned.
      //
      // A pin releases on `shouldContinue`, which the wheel handler below flips.
      // Anything else that scrolls during the window — arrow keys, touch — is
      // left to the window expiring, which it does within `OPEN_SETTLE_MS`. The
      // alternative is inferring intent from the offset, and the corrections a
      // pin is armed to survive move the scroller exactly the way a reader does.
      if (pinArmedRef.current) return
      updateButtons()
    }

    scrollElement.addEventListener('wheel', handleWheel, { passive: true })
    scrollElement.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollElement.removeEventListener('wheel', handleWheel)
      scrollElement.removeEventListener('scroll', handleScroll)
    }
  }, [scrollElement, getDistanceFromBottom, threshold, updateButtons, recordReadingPosition])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      scrollEndCleanupRef.current?.()
      disarmPin()
    }
  }, [disarmPin])

  return {
    showScrollButton,
    scrollToBottom,
    pinToBottom,
    onContentChange,
    stopFollowing,
    scrollRef,
  }
}
